import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
    TextField,
    Paper,
    Breadcrumbs,
    Link,
    TablePagination,
    Alert,
    Snackbar,
    CircularProgress,
    Drawer,
    Dialog,
    DialogTitle,
    DialogContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useMediaQuery,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Menu,
    ListItemIcon,
    Stack,
    Grid,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloseIcon from '@mui/icons-material/Close';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PublicIcon from '@mui/icons-material/Public';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FilterListIcon from '@mui/icons-material/FilterList';
import HandymanIcon from '@mui/icons-material/Handyman';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
    DirectoryTree,
    AssetGrid,
    AssetTable,
    UploadDialog,
    DeleteConfirmDialog,
    CreateFolderDialog,
    AssetCarouselDialog,
    MoveDestinationDialog,
    AssetStorageUsageBar,
    FolderPropertiesDialog,
    AssetPropertiesDialog,
} from './components';

import {
    Asset,
    FolderTreeResponse,
    ViewMode,
    SortConfig,
    SortField,
    SortOrder,
    FilterConfig,
    UploadDialogState,
    DeleteDialogState,
    CreateFolderDialogState,
    FolderPropertiesDialogState,
    AssetPropertiesDialogState,
    AssetType,
    StorageUsage,
    folderDisplayName,
} from './AssetExplorer.types';

import {
    isProfessionalLightTheme,
    professionalLightBorderedIconButtonSx,
    professionalLightViewToggleGroupSx,
    PROFESSIONAL_LIGHT_ACCENT,
    PROFESSIONAL_LIGHT_COLLAPSED_STRIP_BG,
    PROFESSIONAL_LIGHT_COLLAPSED_STRIP_HOVER_BG,
} from './assetExplorerTheme';

import {
    buildFolderBreadcrumbPath,
    PersistedAssetExplorerUiState,
    readPersistedAssetExplorerUiState as readPersistedUiState,
    writePersistedAssetExplorerUiState as writePersistedUiState,
} from './assetExplorerUiState';

import {
    AssetExplorerConfigProvider,
    AssetExplorerProps,
    useAssetExplorerConfig,
    useResolvedAssetExplorerConfig,
} from './AssetExplorerConfig';

/** Sort subfolders in the main panel using the same Sort By / Order as assets. */
function sortExplorerFolders(
    list: FolderTreeResponse[],
    field: SortField,
    order: SortOrder,
): FolderTreeResponse[] {
    const mul = order === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
        switch (field) {
            case 'name':
                return mul * folderDisplayName(a).localeCompare(folderDisplayName(b), undefined, { sensitivity: 'base' });
            case 'createdAt':
                return mul * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            case 'updatedAt':
                return mul * (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());
            case 'size':
                return mul * ((a.assetCount ?? 0) - (b.assetCount ?? 0));
            case 'type':
            default:
                return mul * folderDisplayName(a).localeCompare(folderDisplayName(b), undefined, { sensitivity: 'base' });
        }
    });
}

function findFolderNode(nodes: FolderTreeResponse[], id: string): FolderTreeResponse | null {
    for (const n of nodes) {
        if (n._id === id) return n;
        const c = findFolderNode(n.children || [], id);
        if (c) return c;
    }
    return null;
}

function addSubtreeFolderIds(node: FolderTreeResponse, into: Set<string>) {
    into.add(node._id);
    (node.children || []).forEach((ch) => addSubtreeFolderIds(ch, into));
}

function collectSubtreeIdsForSelectedFolders(folders: FolderTreeResponse[], selectedFolderIds: Set<string>): Set<string> {
    const ids = new Set<string>();
    selectedFolderIds.forEach((fid) => {
        const node = findFolderNode(folders, fid);
        if (node) addSubtreeFolderIds(node, ids);
    });
    return ids;
}

function rootsOfFolderPaths(selectedDocs: FolderTreeResponse[]): FolderTreeResponse[] {
    const sorted = [...selectedDocs].sort((a, b) => a.path.length - b.path.length);
    const roots: FolderTreeResponse[] = [];
    for (const f of sorted) {
        const under = roots.some((r) => f.path !== r.path && f.path.startsWith(`${r.path}/`));
        if (under) continue;
        roots.push(f);
    }
    return roots;
}

/**
 * Shared folder/asset explorer.
 *
 * Mounted by Asset Management (`/#/asset-management/asset-explorer`, API `/ae`) and
 * Beam on Screen (`/#/beamonscreen/assets`, API `/bos`). All props are optional; with no props it
 * behaves exactly like the original Asset Management explorer.
 */
export const AssetExplorer: React.FC<AssetExplorerProps> = (props) => {
    const config = useResolvedAssetExplorerConfig(props);
    return (
        <AssetExplorerConfigProvider config={config}>
            <AssetExplorerView />
        </AssetExplorerConfigProvider>
    );
};

const AssetExplorerView: React.FC = () => {
    const {
        api,
        title: explorerTitle,
        uiStateStorageKey,
        sidebarWidthStorageKey: SIDEBAR_WIDTH_STORAGE_KEY,
    } = useAssetExplorerConfig();
    const {
        getFolderTree,
        getAssets,
        getChildFolders,
        bulkDeleteAssets,
        deleteFolder,
        getDownloadUrl,
        moveExplorerItems,
        publishExplorerItems,
        unpublishExplorerItems,
        shareExplorerFolders,
        unshareExplorerFolders,
        getStorageUsage,
    } = api;
    const readPersistedAssetExplorerUiState = () => readPersistedUiState(uiStateStorageKey);
    const writePersistedAssetExplorerUiState = (state: PersistedAssetExplorerUiState) =>
        writePersistedUiState(state, uiStateStorageKey);

    const theme = useTheme();
    const proLight = isProfessionalLightTheme(theme);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isMobileLandscape = useMediaQuery('(orientation: landscape)') && isMobile;

    /** Same muted pill background as explorer toolbar `<Box sx={{ backgroundColor }}>`. */
    const explorerToolbarMutedBackground = () =>
        theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)';
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    /** Mobile: dialogs for toolbar Row 2 (filters) and Row 1 (actions) */
    const [mobileFiltersDialogOpen, setMobileFiltersDialogOpen] = useState(false);
    const [mobileToolsDialogOpen, setMobileToolsDialogOpen] = useState(false);

    /** Desktop md+: collapsible folders sidebar */
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    /**
     * Desktop md+: user-resizable sidebar width.
     * Width is clamped to [SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH] and persisted to
     * localStorage so the user's chosen width survives reloads. The expanded
     * sidebar uses this width; the collapsed strip continues to render at a
     * fixed thin width.
     */
    const SIDEBAR_MIN_WIDTH = 220;
    const SIDEBAR_MAX_WIDTH = 560;
    const SIDEBAR_DEFAULT_WIDTH = 280;

    const readInitialSidebarWidth = (): number => {
        if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH;
        try {
            const raw = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
            if (raw) {
                const parsed = parseInt(raw, 10);
                if (Number.isFinite(parsed)) {
                    return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, parsed));
                }
            }
        } catch {
            // ignore storage access errors (private mode, etc.)
        }
        return SIDEBAR_DEFAULT_WIDTH;
    };

    const [sidebarWidth, setSidebarWidth] = useState<number>(readInitialSidebarWidth);
    const [isResizingSidebar, setIsResizingSidebar] = useState(false);
    const resizingSidebarRef = useRef(false);
    const resizeStartXRef = useRef(0);
    const resizeStartWidthRef = useRef(SIDEBAR_DEFAULT_WIDTH);
    const mainContainerRef = useRef<HTMLDivElement | null>(null);

    const clampSidebarWidth = useCallback((next: number): number => {
        const container = mainContainerRef.current;
        // Reserve room for the main content panel; never let the sidebar
        // squash the main area below ~320px.
        const containerMax = container
            ? Math.max(SIDEBAR_MIN_WIDTH, container.clientWidth - 320)
            : SIDEBAR_MAX_WIDTH;
        const upper = Math.min(SIDEBAR_MAX_WIDTH, containerMax);
        return Math.min(upper, Math.max(SIDEBAR_MIN_WIDTH, next));
    }, []);

    const handleSidebarResizeStart = useCallback(
        (clientX: number) => {
            resizingSidebarRef.current = true;
            resizeStartXRef.current = clientX;
            resizeStartWidthRef.current = sidebarWidth;
            setIsResizingSidebar(true);
        },
        [sidebarWidth],
    );

    useEffect(() => {
        if (!isResizingSidebar) return;

        const onMouseMove = (e: MouseEvent) => {
            if (!resizingSidebarRef.current) return;
            const delta = e.clientX - resizeStartXRef.current;
            setSidebarWidth(clampSidebarWidth(resizeStartWidthRef.current + delta));
        };
        const onTouchMove = (e: TouchEvent) => {
            if (!resizingSidebarRef.current) return;
            const touch = e.touches[0];
            if (!touch) return;
            const delta = touch.clientX - resizeStartXRef.current;
            setSidebarWidth(clampSidebarWidth(resizeStartWidthRef.current + delta));
        };
        const endResize = () => {
            if (!resizingSidebarRef.current) return;
            resizingSidebarRef.current = false;
            setIsResizingSidebar(false);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', endResize);
        document.addEventListener('touchmove', onTouchMove, { passive: true });
        document.addEventListener('touchend', endResize);
        document.addEventListener('touchcancel', endResize);

        const prevCursor = document.body.style.cursor;
        const prevUserSelect = document.body.style.userSelect;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', endResize);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', endResize);
            document.removeEventListener('touchcancel', endResize);
            document.body.style.cursor = prevCursor;
            document.body.style.userSelect = prevUserSelect;
        };
    }, [isResizingSidebar, clampSidebarWidth]);

    // Persist the final width whenever resizing ends.
    useEffect(() => {
        if (isResizingSidebar) return;
        try {
            window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidth));
        } catch {
            // ignore
        }
    }, [isResizingSidebar, sidebarWidth]);

    // Reset width on double-click of the resize handle.
    const handleSidebarResizeReset = useCallback(() => {
        setSidebarWidth(clampSidebarWidth(SIDEBAR_DEFAULT_WIDTH));
    }, [clampSidebarWidth]);

    // Data State
    const [folders, setFolders] = useState<FolderTreeResponse[]>([]);
    /** Immediate children of selectedFolder for main panel (authoritative; refreshed with assets). */
    const [childFoldersRaw, setChildFoldersRaw] = useState<FolderTreeResponse[]>([]);
    const [assets, setAssets] = useState<Asset[]>([]);
    const [selectedFolder, setSelectedFolder] = useState<FolderTreeResponse | null>(null);
    const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
    const [selectedFolderIds, setSelectedFolderIds] = useState<Set<string>>(new Set());
    const [breadcrumbPath, setBreadcrumbPath] = useState<FolderTreeResponse[]>([]);

    // UI State (read from localStorage on each mount — not module scope)
    const [viewMode, setViewMode] = useState<ViewMode>(
        () => readPersistedAssetExplorerUiState().viewMode ?? 'grid',
    );
    const [loading, setLoading] = useState(true);
    const [assetsLoading, setAssetsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalAssets, setTotalAssets] = useState(0);
    const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);

    // Sort State
    const [sortConfig, setSortConfig] = useState<SortConfig>(() => {
        const persisted = readPersistedAssetExplorerUiState().sortConfig;
        return (
            persisted ?? {
                field: 'createdAt',
                order: 'desc',
            }
        );
    });

    // Filter State
    const [filterConfig, setFilterConfig] = useState<FilterConfig>(
        () => readPersistedAssetExplorerUiState().filterConfig ?? {},
    );

    // Dialog States
    const [uploadDialog, setUploadDialog] = useState<UploadDialogState>({
        open: false,
        folderId: '',
    });
    const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
        open: false,
        assetIds: [],
        assetNames: [],
        folderIds: [],
        folderNames: [],
    });
    const [createFolderDialog, setCreateFolderDialog] = useState<CreateFolderDialogState>({
        open: false,
        parentId: null,
    });
    const [folderPropertiesDialog, setFolderPropertiesDialog] = useState<FolderPropertiesDialogState>({
        open: false,
        folderId: '',
    });
    const [assetPropertiesDialog, setAssetPropertiesDialog] = useState<AssetPropertiesDialogState>({
        open: false,
        assetIds: [],
    });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [moveLoading, setMoveLoading] = useState(false);
    const [publishUnpublishLoading, setPublishUnpublishLoading] = useState(false);
    const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null);

    /** Fullscreen preview carousel (grid thumbnails) */
    const [carouselOpen, setCarouselOpen] = useState(false);
    const [carouselIndex, setCarouselIndex] = useState(0);

    // Notification State
    const [notification, setNotification] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info';
    }>({
        open: false,
        message: '',
        severity: 'info',
    });

    useEffect(() => {
        if (!isMobile) {
            setMobileFiltersDialogOpen(false);
            setMobileToolsDialogOpen(false);
        }
    }, [isMobile]);

    // Persist toolbar settings and last visited folder for return visits
    useEffect(() => {
        const payload: PersistedAssetExplorerUiState = {
            viewMode,
            sortConfig,
            filterConfig: filterConfig.type ? { type: filterConfig.type } : {},
        };
        if (selectedFolder?._id) {
            payload.selectedFolderId = selectedFolder._id;
        } else {
            // Avoid wiping stored folder while the tree is still loading on mount
            const existing = readPersistedAssetExplorerUiState();
            if (existing.selectedFolderId) {
                payload.selectedFolderId = existing.selectedFolderId;
            }
        }
        writePersistedAssetExplorerUiState(payload);
    }, [viewMode, sortConfig, filterConfig, selectedFolder?._id]);

    // Load folder tree on mount
    useEffect(() => {
        loadFolderTree();
    }, []);

    // Load child folders + assets when selection or asset pagination/sort changes
    useEffect(() => {
        if (selectedFolder) {
            loadFolderContents();
        }
    }, [selectedFolder?._id, page, rowsPerPage, sortConfig.field, sortConfig.order]);

    const loadStorageUsage = async () => {
        try {
            const usage = await getStorageUsage();
            setStorageUsage(usage);
        } catch {
            // Keep last known usage; the explorer remains usable without the quota bar.
        }
    };

    const loadFolderTree = async () => {
        setLoading(true);
        void loadStorageUsage();
        try {
            const tree = await getFolderTree();
            setFolders(tree);

            if (tree.length > 0) {
                // Restore last folder on first mount; keep selection on Refresh
                setSelectedFolder((current) => {
                    if (current) return current;
                    const restoredId = readPersistedAssetExplorerUiState().selectedFolderId;
                    if (restoredId) {
                        const restored = findFolderNode(tree, restoredId);
                        if (restored) return restored;
                    }
                    return tree[0];
                });
                setBreadcrumbPath((current) => {
                    if (current.length > 0) return current;
                    const restoredId = readPersistedAssetExplorerUiState().selectedFolderId;
                    if (restoredId) {
                        const path = buildFolderBreadcrumbPath(tree, restoredId);
                        if (path.length > 0) return path;
                    }
                    return [tree[0]];
                });
            }
        } catch (error: any) {
            showNotification(error.message || 'Failed to load folders', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadFolderContents = async () => {
        if (!selectedFolder) return;

        setAssetsLoading(true);
        try {
            const [kids, response] = await Promise.all([
                getChildFolders(selectedFolder._id),
                getAssets(selectedFolder._id, page, rowsPerPage, sortConfig.field, sortConfig.order),
            ]);
            setChildFoldersRaw(kids);
            setAssets(response.assets);
            setTotalAssets(response.pagination.total);
        } catch (error: any) {
            showNotification(error.message || 'Failed to load folder contents', 'error');
        } finally {
            setAssetsLoading(false);
        }
    };

    const showNotification = (message: string, severity: 'success' | 'error' | 'info') => {
        setNotification({ open: true, message, severity });
    };

    // Folder Operations
    const handleFolderSelect = (folder: FolderTreeResponse) => {
        setSelectedFolder(folder);
        setSelectedAssets(new Set());
        setSelectedFolderIds(new Set());
        setPage(0);

        // Close mobile drawer when folder is selected
        if (isMobile) {
            setMobileDrawerOpen(false);
        }

        setBreadcrumbPath(buildFolderBreadcrumbPath(folders, folder._id));
    };

    const handleFolderCreate = (parentId: string | null) => {
        setCreateFolderDialog({ open: true, parentId });
    };

    const handleFolderDelete = async (folderId: string) => {
        if (window.confirm('Are you sure you want to delete this folder and all its contents?')) {
            try {
                await deleteFolder(folderId);
                showNotification('Folder deleted successfully', 'success');
                loadFolderTree();
                if (selectedFolder?._id === folderId) {
                    setSelectedFolder(folders[0] || null);
                }
            } catch (error: any) {
                showNotification(error.message || 'Failed to delete folder', 'error');
            }
        }
    };

    const handleFolderToggleSelect = (folderId: string) => {
        setSelectedFolderIds((prev) => {
            const next = new Set(prev);
            if (next.has(folderId)) {
                next.delete(folderId);
            } else {
                next.add(folderId);
            }
            return next;
        });
    };

    // Asset Operations
    const handleAssetSelect = (assetId: string) => {
        setSelectedAssets((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(assetId)) {
                newSet.delete(assetId);
            } else {
                newSet.add(assetId);
            }
            return newSet;
        });
    };

    const handleDeleteSelected = () => {
        const subtreeUnderSelected = collectSubtreeIdsForSelectedFolders(folders, selectedFolderIds);

        const folderNodes = [...selectedFolderIds]
            .map((id) => findFolderNode(folders, id))
            .filter((n): n is FolderTreeResponse => Boolean(n));
        const folderRoots = rootsOfFolderPaths(folderNodes);
        const folderIdsOrdered = [...folderRoots].sort((a, b) => b.path.length - a.path.length).map((f) => f._id);
        const folderNames = folderRoots.map((f) => folderDisplayName(f));

        const assetIds = [...selectedAssets].filter((aid) => {
            const asset = assets.find((a) => a._id === aid);
            if (!asset) return true;
            return !subtreeUnderSelected.has(asset.folderId);
        });
        const assetNames = assetIds
            .map((id) => assets.find((a) => a._id === id)?.name)
            .filter((n): n is string => Boolean(n));

        setDeleteDialog({
            open: true,
            assetIds,
            assetNames,
            folderIds: folderIdsOrdered,
            folderNames,
        });
    };

    const handleConfirmDelete = async () => {
        setDeleteLoading(true);
        try {
            if (deleteDialog.assetIds.length > 0) {
                await bulkDeleteAssets(deleteDialog.assetIds);
            }
            for (const fid of deleteDialog.folderIds) {
                await deleteFolder(fid);
            }
            const parts: string[] = [];
            if (deleteDialog.assetIds.length) parts.push(`${deleteDialog.assetIds.length} asset(s)`);
            if (deleteDialog.folderIds.length) parts.push(`${deleteDialog.folderIds.length} folder(s)`);
            showNotification(`${parts.join(' and ')} deleted successfully`, 'success');
            setDeleteDialog({ open: false, assetIds: [], assetNames: [], folderIds: [], folderNames: [] });
            setSelectedAssets(new Set());
            setSelectedFolderIds(new Set());
            loadFolderTree();
            loadFolderContents();
        } catch (error: any) {
            showNotification(error.message || 'Failed to delete', 'error');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleConfirmMove = async (destinationFolderId: string) => {
        setMoveLoading(true);
        try {
            const subtree = collectSubtreeIdsForSelectedFolders(folders, selectedFolderIds);
            const assetIds = [...selectedAssets].filter((aid) => {
                const asset = assets.find((a) => a._id === aid);
                if (!asset) return true;
                return !subtree.has(asset.folderId);
            });
            const folderNodes = [...selectedFolderIds]
                .map((id) => findFolderNode(folders, id))
                .filter((n): n is FolderTreeResponse => Boolean(n));
            const folderRoots = rootsOfFolderPaths(folderNodes);
            const folderIds = folderRoots.map((f) => f._id);

            await moveExplorerItems({
                destinationFolderId,
                assetIds,
                folderIds,
            });
            showNotification('Items moved successfully', 'success');
            setMoveDialogOpen(false);
            setSelectedAssets(new Set());
            setSelectedFolderIds(new Set());
            await loadFolderTree();
            loadFolderContents();
        } catch (error: any) {
            showNotification(error.message || 'Failed to move items', 'error');
        } finally {
            setMoveLoading(false);
        }
    };

    const handlePublishSelected = async () => {
        setPublishUnpublishLoading(true);
        try {
            await publishExplorerItems({
                assetIds: [...selectedAssets],
                folderIds: [...selectedFolderIds],
            });
            showNotification('Items published successfully', 'success');
            setSelectedAssets(new Set());
            setSelectedFolderIds(new Set());
            await loadFolderTree();
            loadFolderContents();
        } catch (error: any) {
            showNotification(error.message || 'Failed to publish items', 'error');
        } finally {
            setPublishUnpublishLoading(false);
        }
    };

    const handleUnpublishSelected = async () => {
        setPublishUnpublishLoading(true);
        try {
            await unpublishExplorerItems({
                assetIds: [...selectedAssets],
                folderIds: [...selectedFolderIds],
            });
            showNotification('Items unpublished successfully', 'success');
            setSelectedAssets(new Set());
            setSelectedFolderIds(new Set());
            await loadFolderTree();
            loadFolderContents();
        } catch (error: any) {
            showNotification(error.message || 'Failed to unpublish items', 'error');
        } finally {
            setPublishUnpublishLoading(false);
        }
    };

    const handleShareSelected = async (unshare: boolean) => {
        setPublishUnpublishLoading(true);
        try {
            const folderIds = [...selectedFolderIds];
            if (unshare) {
                await unshareExplorerFolders(folderIds);
                showNotification('Folder unshared', 'success');
            } else {
                await shareExplorerFolders(folderIds);
                showNotification('Folder shared with your company', 'success');
            }
            setSelectedFolderIds(new Set());
            await loadFolderTree();
            loadFolderContents();
        } catch (error: any) {
            showNotification(error.message || (unshare ? 'Failed to unshare folder' : 'Failed to share folder'), 'error');
        } finally {
            setPublishUnpublishLoading(false);
        }
    };

    const handleSingleAssetDownload = async (asset: Asset) => {
        try {
            const { downloadUrl, filename } = await getDownloadUrl(asset._id);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            link.click();
        } catch (error: any) {
            showNotification(error.message || 'Failed to download asset', 'error');
        }
    };

    const handleSingleAssetDelete = (asset: Asset) => {
        setDeleteDialog({
            open: true,
            assetIds: [asset._id],
            assetNames: [asset.name],
            folderIds: [],
            folderNames: [],
        });
    };

    // Sort Operations
    const handleSortChange = (field: SortField) => {
        setSortConfig((prev) => ({
            field,
            order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
        }));
        setPage(0);
    };

    // Pagination
    const handlePageChange = (_: any, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const tablePaginationSx = {
        flex: '0 0 auto',
        width: 'auto',
        minWidth: 0,
        overflow: 'visible',
        '& .MuiTablePagination-toolbar': {
            backgroundColor: 'transparent',
            minHeight: 36,
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
            paddingBottom: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'nowrap',
            gap: 1,
            rowGap: 0.75,
            width: 'auto',
        },
        '& .MuiTablePagination-spacer': {
            display: 'none',
        },
        '& .MuiTablePagination-selectLabel': {
            fontSize: '0.8125rem',
            margin: 0,
            alignSelf: 'center',
            lineHeight: 1.2,
            flexShrink: 0,
        },
        '& .MuiTablePagination-displayedRows': {
            fontSize: '0.8125rem',
            margin: 0,
            alignSelf: 'center',
            lineHeight: 1.2,
            flexShrink: 0,
        },
        '& .MuiTablePagination-actions': {
            marginLeft: 0.5,
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'center',
            flexShrink: 0,
        },
        '& .MuiIconButton-root': {
            padding: 0.5,
            alignSelf: 'center',
        },
        '& .MuiTablePagination-select': {
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'center',
            marginLeft: 0,
            marginRight: 0,
        },
        '& .MuiInputBase-root': {
            marginLeft: 0,
            marginRight: 0,
            alignSelf: 'center',
        },
        '& .MuiSelect-select, & .MuiSelect-outlined': {
            py: 0.35,
            fontSize: '0.8125rem',
            display: 'flex',
            alignItems: 'center',
            minHeight: 28,
            boxSizing: 'border-box',
        },
    } as const;

    const renderPaginationFooter = (variant: 'embedded' | 'standalone') => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                minWidth: 0,
                ...(variant === 'embedded'
                    ? {
                        mt: 'auto',
                        pt: 1,
                        py: 0.25,
                        px: 1,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        borderRadius: 0,
                        borderBottomLeftRadius: 8,
                        borderBottomRightRadius: 8,
                        backgroundColor: (t) =>
                            t.palette.mode === 'dark'
                                ? 'rgba(0,0,0,0.35)'
                                : 'rgba(0,0,0,0.04)',
                        flexShrink: 0,
                    }
                    : {
                        mt: 1,
                        py: 0.25,
                        px: 1,
                        borderRadius: 1.5,
                        backgroundColor: (t) =>
                            t.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                        flexShrink: 0,
                    }),
            }}
        >
            <AssetStorageUsageBar usage={storageUsage} />
            <TablePagination
                component="div"
                count={totalAssets}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50, 100]}
                sx={tablePaginationSx}
            />
        </Box>
    );

    // Subfolders: search + sort (client-side; type filter applies to assets only)
    const displayChildFolders = useMemo(() => {
        let list = childFoldersRaw;
        const q = searchQuery.trim().toLowerCase();
        if (q) {
            list = list.filter((f) => folderDisplayName(f).toLowerCase().includes(q) || f.name.toLowerCase().includes(q));
        }
        return sortExplorerFolders(list, sortConfig.field, sortConfig.order);
    }, [childFoldersRaw, searchQuery, sortConfig.field, sortConfig.order]);

    // Filter assets by search query and type (client-side for current page)
    const filteredAssets = useMemo(() => {
        let list = assets;
        const q = searchQuery.trim().toLowerCase();
        if (q) {
            list = list.filter((a) => a.name.toLowerCase().includes(q));
        }
        const typeFilter = filterConfig.type;
        if (typeFilter && typeFilter !== 'ALL') {
            list = list.filter((a) => a.type === typeFilter);
        }
        return list;
    }, [assets, searchQuery, filterConfig.type]);

    const applyDisplayedSelection = useCallback((folderIds: string[], assetIds: string[]) => {
        setSelectedFolderIds(new Set(folderIds));
        setSelectedAssets(new Set(assetIds));
    }, []);

    const handleToolbarSelectAll = useCallback(() => {
        const folderIds = displayChildFolders.map((f) => f._id);
        const assetIds = filteredAssets.map((a) => a._id);
        const total = folderIds.length + assetIds.length;
        let selectedVisible = 0;
        for (const id of folderIds) {
            if (selectedFolderIds.has(id)) selectedVisible++;
        }
        for (const id of assetIds) {
            if (selectedAssets.has(id)) selectedVisible++;
        }
        if (total > 0 && selectedVisible === total) {
            applyDisplayedSelection([], []);
        } else {
            applyDisplayedSelection(folderIds, assetIds);
        }
    }, [
        displayChildFolders,
        filteredAssets,
        selectedFolderIds,
        selectedAssets,
        applyDisplayedSelection,
    ]);

    const toolbarSelectAllEnabled =
        Boolean(selectedFolder) && (displayChildFolders.length > 0 || filteredAssets.length > 0);

    const invalidMoveDestinationIds = useMemo(
        () => collectSubtreeIdsForSelectedFolders(folders, selectedFolderIds),
        [folders, selectedFolderIds],
    );

    const selectionCount = selectedAssets.size + selectedFolderIds.size;

    const bulkSelectionBusy = publishUnpublishLoading || deleteLoading;

    /** Keeps outlined semantic colors visible when MUI would gray disabled buttons. */
    const outlinedMaintainedDisabledSx = (paletteKey: 'secondary' | 'success' | 'warning' | 'error') => {
        const c = theme.palette[paletteKey];
        const isLight = theme.palette.mode === 'light';
        const secondaryLightDisabled = paletteKey === 'secondary' && isLight;

        const disabledLabel = secondaryLightDisabled
            ? alpha(theme.palette.grey[900], 0.9)
            : alpha(c.dark, 0.72);
        const disabledIcon = secondaryLightDisabled
            ? alpha(theme.palette.grey[800], 0.85)
            : alpha(c.main, 0.58);
        const disabledBorder = secondaryLightDisabled
            ? alpha(theme.palette.grey[700], 0.5)
            : alpha(c.main, 0.45);

        return {
            borderWidth: 2,
            '&:hover:not(.Mui-disabled)': { borderWidth: 2 },
            '&.Mui-disabled': {
                opacity: 1,
                backgroundColor: 'transparent',
                backgroundImage: 'none',
                boxShadow: 'none',
                borderWidth: 2,
                borderStyle: 'solid',
                borderColor: disabledBorder,
                color: disabledLabel,
                WebkitTextFillColor: disabledLabel,
                '& .MuiSvgIcon-root': {
                    color: disabledIcon,
                },
            },
            '&.Mui-disabled:hover': {
                backgroundColor: 'transparent',
                backgroundImage: 'none',
                boxShadow: 'none',
            },
        };
    };

    /** Move: contained secondary — ensure readable label on light theme if contrastText is weak. */
    const moveContainedReadableSx =
        theme.palette.mode === 'light'
            ? {
                  color: alpha(theme.palette.grey[900], 0.92),
                  '& .MuiSvgIcon-root': { color: alpha(theme.palette.grey[900], 0.92) },
              }
            : {};

    /** Row 1 toolbar buttons — compact but readable (aligned heights across Upload). */
    const toolbarRow1CompactBtnSx = {
        minHeight: 34,
        py: 0.5,
        px: 1.25,
        fontSize: '0.8125rem',
        lineHeight: 1.25,
        borderRadius: 1,
        textTransform: 'none' as const,
        '& .MuiButton-startIcon': {
            marginRight: '6px',
            marginLeft: '-2px',
            '& > *:nth-of-type(1)': { fontSize: 18 },
        },
    };

    const handleThumbnailClick = (asset: Asset) => {
        const idx = filteredAssets.findIndex((a) => a._id === asset._id);
        if (idx >= 0) {
            setCarouselIndex(idx);
            setCarouselOpen(true);
        }
    };

    const closeCreateMenu = () => setCreateMenuAnchor(null);

    const handleCreateFolderFromMenu = () => {
        closeCreateMenu();
        handleFolderCreate(selectedFolder ? selectedFolder._id : null);
    };

    const handleUploadFileFromMenu = () => {
        closeCreateMenu();
        setUploadDialog({
            open: true,
            folderId: selectedFolder?._id || '',
        });
    };

    const handleOpenFolderProperties = (folderId: string) => {
        setFolderPropertiesDialog({ open: true, folderId });
    };

    const handleOpenSelectionProperties = () => {
        if (selectedFolderIds.size === 1 && selectedAssets.size === 0) {
            const folderId = [...selectedFolderIds][0];
            setFolderPropertiesDialog({ open: true, folderId });
            return;
        }
        if (selectedAssets.size >= 1 && selectedFolderIds.size === 0) {
            setAssetPropertiesDialog({ open: true, assetIds: [...selectedAssets] });
        }
    };

    const showPropertiesButton =
        (selectedFolderIds.size === 1 && selectedAssets.size === 0) ||
        (selectedAssets.size >= 1 && selectedFolderIds.size === 0);

    const explorerToolbarBatchButtons = (buttonFullWidth: boolean): React.ReactElement[] => {
        if (selectionCount === 0 || selectedFolder?.shared) return [];
        const selectedFolderNodes = [...selectedFolderIds]
            .map((id) => findFolderNode(folders, id))
            .filter((folder): folder is FolderTreeResponse => Boolean(folder));
        if (selectedFolderNodes.some((folder) => folder.shared)) return [];
        const shareFolders = selectedFolderNodes.filter((folder) => !folder.isRoot);
        const canShareSelection = shareFolders.length > 0 && shareFolders.every((folder) => folder.published);
        const alreadyShared = canShareSelection && shareFolders.every((folder) => folder.sharedWithCompany);

        const propertiesLabel =
            selectedAssets.size > 1 ? `Properties (${selectedAssets.size})` : 'Properties';

        const propertiesButton = showPropertiesButton ? (
            <Button
                key="ae-properties"
                fullWidth={buttonFullWidth}
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<SettingsOutlinedIcon />}
                onClick={handleOpenSelectionProperties}
                disabled={bulkSelectionBusy}
                sx={{
                    ...toolbarRow1CompactBtnSx,
                    borderWidth: 2,
                    '&:hover:not(.Mui-disabled)': { borderWidth: 2 },
                }}
            >
                {propertiesLabel}
            </Button>
        ) : null;

        return [
            ...(propertiesButton ? [propertiesButton] : []),
            <Button
                key="ae-move"
                fullWidth={buttonFullWidth}
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<DriveFileMoveIcon />}
                onClick={() => setMoveDialogOpen(true)}
                disabled={bulkSelectionBusy}
                sx={{
                    ...toolbarRow1CompactBtnSx,
                    boxShadow: 'none',
                    ...moveContainedReadableSx,
                    '&:hover': {
                        boxShadow: 'none',
                        bgcolor: alpha(theme.palette.secondary.main, 0.92),
                        ...moveContainedReadableSx,
                    },
                }}
            >
                Move ({selectionCount})
            </Button>,
            <Button
                key="ae-publish"
                fullWidth={buttonFullWidth}
                variant="outlined"
                color="success"
                size="small"
                startIcon={
                    publishUnpublishLoading ? (
                        <CircularProgress size={14} thickness={5} color="inherit" />
                    ) : (
                        <PublicIcon />
                    )
                }
                onClick={handlePublishSelected}
                disabled={bulkSelectionBusy}
                sx={{
                    ...toolbarRow1CompactBtnSx,
                    borderWidth: 2,
                    '&:hover:not(.Mui-disabled)': { borderWidth: 2 },
                }}
            >
                Publish ({selectionCount})
            </Button>,
            <Button
                key="ae-unpublish"
                fullWidth={buttonFullWidth}
                variant="outlined"
                color="warning"
                size="small"
                startIcon={
                    publishUnpublishLoading ? (
                        <CircularProgress size={14} thickness={5} color="inherit" />
                    ) : (
                        <PublicOffIcon />
                    )
                }
                onClick={handleUnpublishSelected}
                disabled={bulkSelectionBusy}
                sx={{
                    ...toolbarRow1CompactBtnSx,
                    borderWidth: 2,
                    '&:hover:not(.Mui-disabled)': { borderWidth: 2 },
                }}
            >
                Unpublish ({selectionCount})
            </Button>,
            ...(canShareSelection
                ? [
                    <Button
                        key="ae-share"
                        fullWidth={buttonFullWidth}
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<ShareOutlinedIcon />}
                        onClick={() => void handleShareSelected(alreadyShared)}
                        disabled={bulkSelectionBusy}
                        sx={{
                            ...toolbarRow1CompactBtnSx,
                            borderWidth: 2,
                            '&:hover:not(.Mui-disabled)': { borderWidth: 2 },
                        }}
                    >
                        {alreadyShared ? `Unshare (${shareFolders.length})` : `Share (${shareFolders.length})`}
                    </Button>,
                ]
                : []),
            <Button
                key="ae-delete"
                fullWidth={buttonFullWidth}
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleDeleteSelected}
                disabled={bulkSelectionBusy}
                sx={{
                    ...toolbarRow1CompactBtnSx,
                    borderWidth: 2,
                    '&:hover:not(.Mui-disabled)': { borderWidth: 2 },
                }}
            >
                Delete ({selectionCount})
            </Button>,
        ];
    };

    const explorerToolbarTrailingButtons = (buttonFullWidth: boolean): React.ReactElement[] => [
        <Button
            key="ae-selectall"
            fullWidth={buttonFullWidth}
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<SelectAllIcon />}
            onClick={handleToolbarSelectAll}
            disabled={!toolbarSelectAllEnabled}
            sx={{
                ...toolbarRow1CompactBtnSx,
                ...(toolbarSelectAllEnabled
                    ? {
                          borderWidth: 2,
                          '&:hover:not(.Mui-disabled)': { borderWidth: 2 },
                      }
                    : outlinedMaintainedDisabledSx('secondary')),
            }}
        >
            Select All
        </Button>,
        <Button
            key="ae-create"
            fullWidth={buttonFullWidth}
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<AddIcon />}
            endIcon={<ArrowDropDownIcon />}
            aria-haspopup="menu"
            aria-expanded={Boolean(createMenuAnchor)}
            aria-controls={createMenuAnchor ? 'ae-create-menu' : undefined}
            onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
            disabled={!selectedFolder || Boolean(selectedFolder.shared)}
            sx={{
                ...toolbarRow1CompactBtnSx,
                boxShadow: 'none',
                '&:hover': {
                    boxShadow: 'none',
                    bgcolor: alpha(theme.palette.secondary.main, 0.92),
                },
                '& .MuiButton-endIcon': {
                    marginLeft: '2px',
                    marginRight: '-4px',
                    '& > *:nth-of-type(1)': { fontSize: 20 },
                },
            }}
        >
            Create
        </Button>,
    ];

    const explorerToolbarToolButtons = (buttonFullWidth: boolean): React.ReactElement[] => [
        ...explorerToolbarBatchButtons(buttonFullWidth),
        ...explorerToolbarTrailingButtons(buttonFullWidth),
    ];

    const renderAeToolbarRow1CombinedDesktop = () => {
        const batchBtns = explorerToolbarBatchButtons(false);
        const trailingBtns = explorerToolbarTrailingButtons(false);
        return (
            <>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {batchBtns}
                </Box>
                <Box
                    sx={{
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                        ml: { xs: 0, sm: 'auto' },
                        flexWrap: 'wrap',
                    }}
                >
                    {trailingBtns}
                </Box>
            </>
        );
    };

    const renderAeToolbarRow2SortByControl = ({ fullWidth = false }: { fullWidth?: boolean } = {}) => (
        <FormControl fullWidth={fullWidth} sx={fullWidth ? { flex: '1 1 0%', minWidth: 0 } : { minWidth: 140 }} size="small">
            <InputLabel>Sort By</InputLabel>
            <Select
                value={sortConfig.field}
                label="Sort By"
                color="secondary"
                onChange={(e) => {
                    setSortConfig((prev) => ({
                        ...prev,
                        field: e.target.value as SortField,
                    }));
                    setPage(0);
                }}
            >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="createdAt">Uploaded</MenuItem>
                <MenuItem value="updatedAt">Updated</MenuItem>
                <MenuItem value="size">Size</MenuItem>
                <MenuItem value="type">Type</MenuItem>
            </Select>
        </FormControl>
    );

    const renderAeToolbarRow2OrderControl = ({ fullWidth = false }: { fullWidth?: boolean } = {}) => (
        <FormControl fullWidth={fullWidth} sx={fullWidth ? { flex: '1 1 0%', minWidth: 0 } : { minWidth: 100 }} size="small">
            <InputLabel>Order</InputLabel>
            <Select
                value={sortConfig.order}
                label="Order"
                color="secondary"
                onChange={(e) => {
                    setSortConfig((prev) => ({
                        ...prev,
                        order: e.target.value as SortConfig['order'],
                    }));
                    setPage(0);
                }}
            >
                <MenuItem value="asc">ASC</MenuItem>
                <MenuItem value="desc">DSC</MenuItem>
            </Select>
        </FormControl>
    );

    const renderAeToolbarRow2TypeFilterControl = ({ fullWidth = false }: { fullWidth?: boolean } = {}) => (
        <FormControl fullWidth={fullWidth} sx={fullWidth ? { flex: '1 1 0%', minWidth: 0 } : { minWidth: 130 }} size="small">
            <InputLabel>Type Filter</InputLabel>
            <Select
                value={filterConfig.type ?? 'ALL'}
                label="Type Filter"
                color="secondary"
                onChange={(e) =>
                    setFilterConfig((prev) => ({
                        ...prev,
                        type: e.target.value as AssetType | 'ALL',
                    }))
                }
            >
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="IMAGE">Image</MenuItem>
                <MenuItem value="VIDEO">Video</MenuItem>
                <MenuItem value="AUDIO">Audio</MenuItem>
                <MenuItem value="ZIP">Zip</MenuItem>
                <MenuItem value="DOCUMENT">Document</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
            </Select>
        </FormControl>
    );

    const renderAeToolbarRow2RefreshControl = ({ iconOnly }: { iconOnly?: boolean } = {}) => (
        iconOnly ? (
            <Tooltip title="Refresh">
                <IconButton
                    color={proLight ? undefined : 'secondary'}
                    onClick={() => {
                        loadFolderTree();
                        loadFolderContents();
                    }}
                    size="small"
                    sx={professionalLightBorderedIconButtonSx(theme)}
                >
                    <RefreshIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        ) : (
            <Button
                variant="outlined"
                color="secondary"
                size="medium"
                startIcon={<RefreshIcon />}
                onClick={() => {
                    loadFolderTree();
                    loadFolderContents();
                }}
                sx={{ textTransform: 'none', flex: '1 1 0%', minWidth: 0 }}
            >
                Refresh
            </Button>
        )
    );

    const renderAeToolbarRow2ViewToggle = () => (
        <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size="small"
            sx={professionalLightViewToggleGroupSx(theme)}
        >
            <ToggleButton value="grid">
                <Tooltip title="Grid View">
                    <GridViewIcon fontSize="small" />
                </Tooltip>
            </ToggleButton>
            <ToggleButton value="table">
                <Tooltip title="Table View">
                    <TableRowsIcon fontSize="small" />
                </Tooltip>
            </ToggleButton>
        </ToggleButtonGroup>
    );


    const renderAeToolbarRow2SearchField = () => (
        <TextField
            label="Search folders & assets"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            color="secondary"
            sx={{
                flex: { sm: '1 1 0%' },
                minWidth: { sm: 0 },
                width: '100%',
            }}
        />
    );

    const renderAeToolbarRow2ControlsRow = (opts?: { includeViewToggle?: boolean }) => {
        const includeViewToggle = opts?.includeViewToggle !== false;
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1,
                    flexWrap: 'wrap',
                    flexShrink: 0,
                    width: { xs: '100%', sm: 'auto' },
                }}
            >
                {renderAeToolbarRow2SortByControl()}
                {renderAeToolbarRow2OrderControl()}
                {renderAeToolbarRow2TypeFilterControl()}
                {renderAeToolbarRow2RefreshControl({ iconOnly: true })}
                {includeViewToggle ? renderAeToolbarRow2ViewToggle() : null}
            </Box>
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: '10px', md: '20px' }, height: '100%' }}>
            {/* Main Content */}
            <Box
                ref={mainContainerRef}
                sx={{
                    display: 'flex',
                    // When the sidebar is expanded on desktop, the resize handle
                    // provides the visual separation, so we drop the flex gap.
                    gap: { xs: 2, md: sidebarCollapsed ? 2 : 0 },
                    height: 'calc(105vh - 200px)',
                }}
            >
                {/* Sidebar - Directory Tree (desktop); collapsible to thin strip */}
                {sidebarCollapsed ? (
                    <Paper
                        elevation={0}
                        component="button"
                        type="button"
                        aria-label={`Expand ${explorerTitle} panel`}
                        onClick={() => setSidebarCollapsed(false)}
                        sx={{
                            width: 44,
                            minWidth: 44,
                            flexShrink: 0,
                            alignSelf: 'stretch',
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            userSelect: 'none',
                            overflow: 'hidden',
                            p: 0.5,
                            bgcolor: proLight
                                ? PROFESSIONAL_LIGHT_COLLAPSED_STRIP_BG
                                : alpha(theme.palette.secondary.main, 0.06),
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                                bgcolor: proLight
                                    ? PROFESSIONAL_LIGHT_COLLAPSED_STRIP_HOVER_BG
                                    : alpha(theme.palette.secondary.main, 0.14),
                            },
                            '&:focus-visible': {
                                outline: `2px solid ${proLight ? PROFESSIONAL_LIGHT_ACCENT : theme.palette.secondary.main}`,
                                outlineOffset: 2,
                            },
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 700,
                                color: proLight ? PROFESSIONAL_LIGHT_ACCENT : 'secondary.main',
                                writingMode: 'vertical-rl',
                                transform: 'rotate(180deg)',
                                textOrientation: 'mixed',
                                letterSpacing: '0.12em',
                                fontSize: '0.8125rem',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {explorerTitle}
                        </Typography>
                    </Paper>
                ) : (
                    <>
                        <Paper
                            elevation={0}
                            sx={{
                                width: sidebarWidth,
                                minWidth: SIDEBAR_MIN_WIDTH,
                                maxWidth: SIDEBAR_MAX_WIDTH,
                                flexShrink: 0,
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                overflow: 'auto',
                                display: { xs: 'none', md: 'block' },
                                transition: isResizingSidebar
                                    ? 'none'
                                    : 'width 0.15s ease',
                            }}
                        >
                            <DirectoryTree
                                folders={folders}
                                selectedFolderId={selectedFolder?._id || null}
                                onFolderSelect={handleFolderSelect}
                                onFolderCreate={handleFolderCreate}
                                onFolderDelete={handleFolderDelete}
                                onFolderProperties={handleOpenFolderProperties}
                                loading={loading}
                                onCollapseSidebar={() => setSidebarCollapsed(true)}
                            />
                        </Paper>

                        {/* Draggable resize handle between sidebar and main area */}
                        <Box
                            role="separator"
                            aria-orientation="vertical"
                            aria-label={`Resize ${explorerTitle} panel`}
                            aria-valuenow={Math.round(sidebarWidth)}
                            aria-valuemin={SIDEBAR_MIN_WIDTH}
                            aria-valuemax={SIDEBAR_MAX_WIDTH}
                            tabIndex={0}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSidebarResizeStart(e.clientX);
                            }}
                            onTouchStart={(e) => {
                                const t = e.touches[0];
                                if (t) handleSidebarResizeStart(t.clientX);
                            }}
                            onDoubleClick={handleSidebarResizeReset}
                            onKeyDown={(e) => {
                                if (e.key === 'ArrowLeft') {
                                    e.preventDefault();
                                    setSidebarWidth((w) =>
                                        clampSidebarWidth(w - (e.shiftKey ? 32 : 12)),
                                    );
                                } else if (e.key === 'ArrowRight') {
                                    e.preventDefault();
                                    setSidebarWidth((w) =>
                                        clampSidebarWidth(w + (e.shiftKey ? 32 : 12)),
                                    );
                                } else if (e.key === 'Home') {
                                    e.preventDefault();
                                    setSidebarWidth(clampSidebarWidth(SIDEBAR_MIN_WIDTH));
                                } else if (e.key === 'End') {
                                    e.preventDefault();
                                    setSidebarWidth(clampSidebarWidth(SIDEBAR_MAX_WIDTH));
                                } else if (
                                    e.key === 'Enter' ||
                                    e.key === ' ' ||
                                    e.key === 'Spacebar'
                                ) {
                                    e.preventDefault();
                                    handleSidebarResizeReset();
                                }
                            }}
                            sx={{
                                position: 'relative',
                                flexShrink: 0,
                                width: 14,
                                mx: 0.5,
                                cursor: 'col-resize',
                                display: { xs: 'none', md: 'flex' },
                                alignItems: 'center',
                                justifyContent: 'center',
                                touchAction: 'none',
                                outline: 'none',
                                '&:focus-visible .ae-resize-grip': {
                                    boxShadow: `0 0 0 2px ${theme.palette.secondary.main}`,
                                    color: theme.palette.secondary.main,
                                },
                                '&:hover .ae-resize-grip': {
                                    opacity: 1,
                                    color: theme.palette.secondary.main,
                                    borderColor: alpha(theme.palette.secondary.main, 0.6),
                                },
                                '&:hover .ae-resize-line': {
                                    backgroundColor:
                                        theme.palette.mode === 'dark'
                                            ? theme.palette.divider
                                            : alpha(theme.palette.divider, 0.85),
                                },
                            }}
                        >
                            <Box
                                className="ae-resize-line"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 2,
                                    borderRadius: 1,
                                    backgroundColor:
                                        theme.palette.mode === 'dark'
                                            ? theme.palette.divider
                                            : isResizingSidebar
                                              ? alpha(theme.palette.secondary.main, 0.7)
                                              : alpha(theme.palette.secondary.main, 0.55),
                                    transition: 'background-color 0.15s ease',
                                    pointerEvents: 'none',
                                }}
                            />
                            <Box
                                className="ae-resize-grip"
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: 18,
                                    height: 38,
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.background.paper,
                                    border: `1px solid ${
                                        isResizingSidebar
                                            ? alpha(theme.palette.secondary.main, 0.7)
                                            : theme.palette.divider
                                    }`,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                                    color: isResizingSidebar
                                        ? theme.palette.secondary.main
                                        : theme.palette.text.secondary,
                                    opacity: isResizingSidebar ? 1 : 0.85,
                                    transition:
                                        'opacity 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                                    pointerEvents: 'none',
                                }}
                            >
                                <DragIndicatorIcon sx={{ fontSize: 18 }} />
                            </Box>
                        </Box>
                    </>
                )}

                {/* Main Content Area */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {(() => {
                        const explorerToolbarSurface = (
                            <Box
                                sx={{
                                    mb: { xs: 0, md: 2 },
                                    p: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: explorerToolbarMutedBackground(),
                                }}
                            >
                        {/* Desktop-only breadcrumbs */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mb: 1.5 }}>
                            <Breadcrumbs sx={{ flex: 1 }}>
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => folders[0] && handleFolderSelect(folders[0])}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        color: theme.palette.text.secondary,
                                        '&:hover': { color: theme.palette.secondary.main },
                                    }}
                                >
                                    <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                    My Assets
                                </Link>
                                {breadcrumbPath.slice(1).map((folder, index) => (
                                    <Link
                                        key={folder._id}
                                        component="button"
                                        variant="body2"
                                        onClick={() => handleFolderSelect(folder)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            color:
                                                index === breadcrumbPath.length - 2
                                                    ? theme.palette.text.primary
                                                    : theme.palette.text.secondary,
                                            fontWeight: index === breadcrumbPath.length - 2 ? 600 : 400,
                                            '&:hover': { color: theme.palette.secondary.main },
                                        }}
                                    >
                                        <FolderIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                        {folderDisplayName(folder)}
                                    </Link>
                                ))}
                            </Breadcrumbs>
                        </Box>

                        {/* Mobile compact toolbar: folder tree + Filter / Tools */}
                        <Box
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                            }}
                        >
                            <Tooltip title="Folder tree">
                                <IconButton
                                    color={proLight ? undefined : 'secondary'}
                                    aria-label="Open folder tree"
                                    onClick={() => setMobileDrawerOpen(true)}
                                    sx={{
                                        border: '2px solid',
                                        borderColor: proLight ? PROFESSIONAL_LIGHT_ACCENT : 'secondary.main',
                                        borderRadius: 1,
                                        color: proLight ? PROFESSIONAL_LIGHT_ACCENT : undefined,
                                        '& .MuiSvgIcon-root': proLight ? { color: PROFESSIONAL_LIGHT_ACCENT } : {},
                                        '&:hover': {
                                            bgcolor: proLight
                                                ? 'rgba(25, 25, 25, 0.06)'
                                                : alpha(theme.palette.secondary.main, 0.08),
                                        },
                                    }}
                                >
                                    <AccountTreeIcon />
                                </IconButton>
                            </Tooltip>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                <Button
                                    variant={mobileFiltersDialogOpen ? 'contained' : 'outlined'}
                                    color="secondary"
                                    size="small"
                                    startIcon={<FilterListIcon />}
                                    onClick={() => {
                                        setMobileToolsDialogOpen(false);
                                        setMobileFiltersDialogOpen(true);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        ...(!mobileFiltersDialogOpen && {
                                            borderWidth: 2,
                                            '&:hover': { borderWidth: 2 },
                                        }),
                                    }}
                                >
                                    Filter
                                </Button>
                                <Button
                                    variant={mobileToolsDialogOpen ? 'contained' : 'outlined'}
                                    color="secondary"
                                    size="small"
                                    startIcon={<HandymanIcon />}
                                    onClick={() => {
                                        setMobileFiltersDialogOpen(false);
                                        setMobileToolsDialogOpen(true);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        ...(!mobileToolsDialogOpen && {
                                            borderWidth: 2,
                                            '&:hover': { borderWidth: 2 },
                                        }),
                                    }}
                                >
                                    Tools
                                </Button>
                            </Box>
                        </Box>

                        {/* Row 1: desktop toolbar row (mobile uses Tools dialog) */}
                        {!isMobile ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                    rowGap: 1,
                                }}
                            >
                                {renderAeToolbarRow1CombinedDesktop()}
                            </Box>
                        ) : null}

                        {/* Row 2: desktop toolbar row (mobile uses Filter dialog) */}
                        {!isMobile ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    gap: { xs: 1.25, sm: 1.5 },
                                    mt: 2.5,
                                    width: '100%',
                                    minWidth: 0,
                                }}
                            >
                                {renderAeToolbarRow2SearchField()}
                                {renderAeToolbarRow2ControlsRow()}
                            </Box>
                        ) : null}

                            </Box>
                        );

                        return isMobile ? (
                            <Accordion
                                defaultExpanded={false}
                                disableGutters
                                elevation={0}
                                sx={{
                                    mb: 2,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    '&:before': { display: 'none' },
                                    overflow: 'hidden',
                                    bgcolor: explorerToolbarMutedBackground(),
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon color="secondary" />}
                                    sx={{
                                        position: 'relative',
                                        minHeight: 44,
                                        bgcolor: explorerToolbarMutedBackground(),
                                        pr: 4,
                                        '& .MuiAccordionSummary-expandIconWrapper': {
                                            position: 'absolute',
                                            right: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                        },
                                        '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                                            transform: 'translateY(-50%) rotate(180deg)',
                                        },
                                        '& .MuiAccordionSummary-content': {
                                            flexGrow: 1,
                                            marginRight: 0,
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            my: 1,
                                        },
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center', width: '100%' }}>
                                        {explorerTitle}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        px: 1.25,
                                        pb: 2,
                                        pt: 0,
                                    }}
                                >
                                    <Box sx={{ mb: 1 }}>
                                        <Breadcrumbs sx={{ flexWrap: 'wrap' }}>
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={() => folders[0] && handleFolderSelect(folders[0])}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    textDecoration: 'none',
                                                    color: theme.palette.text.secondary,
                                                    '&:hover': { color: theme.palette.secondary.main },
                                                }}
                                            >
                                                <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                                My Assets
                                            </Link>
                                            {breadcrumbPath.slice(1).map((folder, index) => (
                                                <Link
                                                    key={folder._id}
                                                    component="button"
                                                    variant="body2"
                                                    onClick={() => handleFolderSelect(folder)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        cursor: 'pointer',
                                                        textDecoration: 'none',
                                                        color:
                                                            index === breadcrumbPath.length - 2
                                                                ? theme.palette.text.primary
                                                                : theme.palette.text.secondary,
                                                        fontWeight:
                                                            index === breadcrumbPath.length - 2 ? 600 : 400,
                                                        '&:hover': { color: theme.palette.secondary.main },
                                                    }}
                                                >
                                                    <FolderIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                                    {folderDisplayName(folder)}
                                                </Link>
                                            ))}
                                        </Breadcrumbs>
                                    </Box>
                                    {explorerToolbarSurface}
                                </AccordionDetails>
                            </Accordion>
                        ) : (
                            explorerToolbarSurface
                        );
                    })()}

                    {isMobile ? (
                        <Paper
                            elevation={0}
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                overflow: 'auto',
                            }}
                        >
                            {viewMode === 'grid' ? (
                                <AssetGrid
                                    childFolders={displayChildFolders}
                                    onFolderOpen={handleFolderSelect}
                                    selectedFolderIds={selectedFolderIds}
                                    onFolderToggleSelect={handleFolderToggleSelect}
                                    assets={filteredAssets}
                                    selectedAssets={selectedAssets}
                                    onAssetSelect={handleAssetSelect}
                                    onThumbnailClick={handleThumbnailClick}
                                    onAssetDownload={handleSingleAssetDownload}
                                    onAssetDelete={handleSingleAssetDelete}
                                    loading={assetsLoading}
                                    readOnly={Boolean(selectedFolder?.shared)}
                                />
                            ) : (
                                <AssetTable
                                    childFolders={displayChildFolders}
                                    onFolderOpen={handleFolderSelect}
                                    selectedFolderIds={selectedFolderIds}
                                    onFolderToggleSelect={handleFolderToggleSelect}
                                    assets={filteredAssets}
                                    selectedAssets={selectedAssets}
                                    onAssetSelect={handleAssetSelect}
                                    onSelectAllDisplayed={applyDisplayedSelection}
                                    sortConfig={sortConfig}
                                    onSortChange={handleSortChange}
                                    filterConfig={filterConfig}
                                    onFilterChange={setFilterConfig}
                                    loading={assetsLoading}
                                />
                            )}
                            {renderPaginationFooter('embedded')}
                        </Paper>
                    ) : (
                        <>
                            <Paper
                                elevation={0}
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    overflow: 'auto',
                                }}
                            >
                                {viewMode === 'grid' ? (
                                    <AssetGrid
                                        childFolders={displayChildFolders}
                                        onFolderOpen={handleFolderSelect}
                                        selectedFolderIds={selectedFolderIds}
                                        onFolderToggleSelect={handleFolderToggleSelect}
                                        assets={filteredAssets}
                                        selectedAssets={selectedAssets}
                                        onAssetSelect={handleAssetSelect}
                                        onThumbnailClick={handleThumbnailClick}
                                        onAssetDownload={handleSingleAssetDownload}
                                        onAssetDelete={handleSingleAssetDelete}
                                        loading={assetsLoading}
                                        readOnly={Boolean(selectedFolder?.shared)}
                                    />
                                ) : (
                                    <AssetTable
                                        childFolders={displayChildFolders}
                                        onFolderOpen={handleFolderSelect}
                                        selectedFolderIds={selectedFolderIds}
                                        onFolderToggleSelect={handleFolderToggleSelect}
                                        assets={filteredAssets}
                                        selectedAssets={selectedAssets}
                                        onAssetSelect={handleAssetSelect}
                                        onSelectAllDisplayed={applyDisplayedSelection}
                                        sortConfig={sortConfig}
                                        onSortChange={handleSortChange}
                                        filterConfig={filterConfig}
                                        onFilterChange={setFilterConfig}
                                        loading={assetsLoading}
                                    />
                                )}
                            </Paper>

                            {renderPaginationFooter('standalone')}
                        </>
                    )}
                </Box>
            </Box>

            {/* Dialogs */}
            <Menu
                id="ae-create-menu"
                anchorEl={createMenuAnchor}
                open={Boolean(createMenuAnchor)}
                onClose={closeCreateMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={handleCreateFolderFromMenu}>
                    <ListItemIcon>
                        <CreateNewFolderIcon fontSize="small" />
                    </ListItemIcon>
                    Create Folder
                </MenuItem>
                <MenuItem onClick={handleUploadFileFromMenu}>
                    <ListItemIcon>
                        <CloudUploadIcon fontSize="small" />
                    </ListItemIcon>
                    Upload File
                </MenuItem>
            </Menu>

            <AssetCarouselDialog
                open={carouselOpen}
                onClose={() => setCarouselOpen(false)}
                assets={filteredAssets}
                initialIndex={carouselIndex}
                onDownload={handleSingleAssetDownload}
            />

            <UploadDialog
                open={uploadDialog.open}
                folderId={uploadDialog.folderId}
                onClose={() => setUploadDialog({ open: false, folderId: '' })}
                onUploadComplete={() => {
                    loadFolderTree();
                    loadFolderContents();
                    showNotification('Assets uploaded successfully', 'success');
                }}
            />

            <DeleteConfirmDialog
                open={deleteDialog.open}
                assetCount={deleteDialog.assetIds.length}
                assetNames={deleteDialog.assetNames}
                folderCount={deleteDialog.folderIds.length}
                folderNames={deleteDialog.folderNames}
                onConfirm={handleConfirmDelete}
                onCancel={() =>
                    setDeleteDialog({
                        open: false,
                        assetIds: [],
                        assetNames: [],
                        folderIds: [],
                        folderNames: [],
                    })
                }
                loading={deleteLoading}
            />

            <MoveDestinationDialog
                open={moveDialogOpen}
                onClose={() => !moveLoading && setMoveDialogOpen(false)}
                folders={folders}
                folderTreeLoading={loading}
                invalidDestinationIds={invalidMoveDestinationIds}
                onConfirm={handleConfirmMove}
                confirmLoading={moveLoading}
            />

            <CreateFolderDialog
                open={createFolderDialog.open}
                parentId={createFolderDialog.parentId}
                onClose={() => setCreateFolderDialog({ open: false, parentId: null })}
                onCreateComplete={() => {
                    loadFolderTree();
                    loadFolderContents();
                    showNotification('Folder created successfully', 'success');
                }}
            />

            <FolderPropertiesDialog
                open={folderPropertiesDialog.open}
                folderId={folderPropertiesDialog.folderId}
                onClose={() => setFolderPropertiesDialog({ open: false, folderId: '' })}
                onSaved={(result) => {
                    loadFolderTree();
                    loadFolderContents();
                    const applied = result?.defaultsAppliedCount;
                    showNotification(
                        applied
                            ? `Folder properties saved · defaults applied to ${applied} assets`
                            : 'Folder properties saved',
                        'success',
                    );
                }}
            />

            <AssetPropertiesDialog
                open={assetPropertiesDialog.open}
                assetIds={assetPropertiesDialog.assetIds}
                onClose={() => setAssetPropertiesDialog({ open: false, assetIds: [] })}
                onSaved={() => {
                    loadFolderContents();
                    showNotification(
                        assetPropertiesDialog.assetIds.length > 1
                            ? `Asset properties saved for ${assetPropertiesDialog.assetIds.length} assets`
                            : 'Asset properties saved',
                        'success',
                    );
                }}
            />

            <Dialog
                open={mobileToolsDialogOpen && isMobile}
                onClose={() => setMobileToolsDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                fullScreen={isMobile}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        pr: 1,
                    }}
                >
                    <Typography variant="h6" component="span">
                        Tools
                    </Typography>
                    <IconButton
                        aria-label="Close tools"
                        size="small"
                        onClick={() => setMobileToolsDialogOpen(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        {explorerToolbarToolButtons(true).map((btn) => (
                            <Grid item xs={isMobileLandscape ? 6 : 12} key={String(btn.key ?? '')}>
                                {btn}
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
            </Dialog>

            <Dialog
                open={mobileFiltersDialogOpen && isMobile}
                onClose={() => setMobileFiltersDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                fullScreen={isMobile}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        pr: 1,
                    }}
                >
                    <Typography variant="h6" component="span">
                        Filter
                    </Typography>
                    <IconButton
                        aria-label="Close filters"
                        size="small"
                        onClick={() => setMobileFiltersDialogOpen(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5} sx={{ pt: 1, width: '100%' }}>
                        <Box sx={{ width: '100%' }}>{renderAeToolbarRow2SearchField()}</Box>
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                alignItems: 'stretch',
                                gap: 2,
                            }}
                        >
                            <Box sx={{ flex: '1 1 0%', minWidth: 0 }}>
                                {renderAeToolbarRow2SortByControl({ fullWidth: true })}
                            </Box>
                            <Box sx={{ flex: '1 1 0%', minWidth: 0 }}>
                                {renderAeToolbarRow2OrderControl({ fullWidth: true })}
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            <Box sx={{ flex: '1 1 0%', minWidth: 0 }}>
                                {renderAeToolbarRow2TypeFilterControl({ fullWidth: true })}
                            </Box>
                            <Box
                                sx={{
                                    flex: '1 1 0%',
                                    minWidth: 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                {renderAeToolbarRow2RefreshControl({ iconOnly: false })}
                            </Box>
                        </Box>
                    </Stack>
                </DialogContent>
            </Dialog>

            {/* Mobile Folder Navigation Drawer */}
            <Drawer
                anchor="left"
                open={mobileDrawerOpen}
                onClose={() => setMobileDrawerOpen(false)}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        width: '85%',
                        maxWidth: 320,
                        borderTopRightRadius: 16,
                        borderBottomRightRadius: 16,
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        px: 1,
                        py: 0.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    }}
                >
                    <IconButton
                        onClick={() => setMobileDrawerOpen(false)}
                        size="small"
                        aria-label="Close folder navigation"
                        sx={{
                            backgroundColor: alpha(theme.palette.action.active, 0.08),
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.action.active, 0.16),
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box sx={{ overflow: 'auto', flex: 1 }}>
                    <DirectoryTree
                        folders={folders}
                        selectedFolderId={selectedFolder?._id || null}
                        onFolderSelect={handleFolderSelect}
                        onFolderCreate={handleFolderCreate}
                        onFolderDelete={handleFolderDelete}
                        onFolderProperties={handleOpenFolderProperties}
                        loading={loading}
                        squareTitleBannerTop
                    />
                </Box>
            </Drawer>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setNotification({ ...notification, open: false })}
                    severity={notification.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AssetExplorer;
